import { ReactNode } from "react";
import { Trans } from "@/shared/i18n";
import { LanguageSelector } from "@/shared/widgets/language-selector";
import { Lightbulb, TriangleAlert } from "lucide-react";

const richComponents = {
  strong: <strong className="font-semibold text-foreground" />,
  em: <em />,
  code: (
    <code className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-primary" />
  ),
};

interface TutorialLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

export function TutorialLayout({ title, subtitle, children }: TutorialLayoutProps) {
  return (
    <div className="min-h-screen bg-[#1A1A1A] text-white">
      <div className="mx-auto max-w-3xl px-6 py-10">
        <header className="mb-10 border-b border-border pb-8">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/app-logo.png" alt="LikeAppro" className="h-8 w-8 rounded" />
              <span className="font-mono text-xs uppercase tracking-widest text-primary">
                LikeAppro
              </span>
            </div>
            <LanguageSelector />
          </div>
          <h1 className="mb-2 text-3xl font-semibold text-foreground">{title}</h1>
          <p className="max-w-xl text-sm text-muted-foreground">{subtitle}</p>
        </header>
        {children}
      </div>
    </div>
  );
}

interface RichTextProps {
  i18nKey: string;
}

export function RichText({ i18nKey }: RichTextProps) {
  return <Trans ns="psdTutorials" i18nKey={i18nKey} components={richComponents} />;
}

interface TutorialScreenshotProps {
  src: string;
  alt: string;
  caption: string;
}

export function TutorialScreenshot({ src, alt, caption }: TutorialScreenshotProps) {
  return (
    <figure className="overflow-hidden rounded-lg border border-border bg-card">
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
      <figcaption className="border-t border-border px-3 py-2 font-mono text-xs text-muted-foreground">
        {caption}
      </figcaption>
    </figure>
  );
}

interface TutorialTipProps {
  variant?: "tip" | "warning";
  children: ReactNode;
}

export function TutorialTip({ variant = "tip", children }: TutorialTipProps) {
  const Icon = variant === "tip" ? Lightbulb : TriangleAlert;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

interface TutorialStepProps {
  number: number;
  title: string;
  isLast?: boolean;
  children: ReactNode;
}

export function TutorialStep({ number, title, isLast = false, children }: TutorialStepProps) {
  return (
    <div className="relative grid grid-cols-[2.75rem_1fr] gap-x-6">
      <div className="flex flex-col items-center">
        <div className="z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-mono text-xs font-medium text-primary">
          {String(number).padStart(2, "0")}
        </div>
        {!isLast && <div className="w-px flex-1 bg-border" />}
      </div>
      <div className="pb-10">
        <h3 className="mb-2 pt-2.5 font-semibold text-foreground">{title}</h3>
        <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

interface TutorialSectionHeaderProps {
  label: string;
  title: string;
  id?: string;
}

export function TutorialSectionHeader({ label, title, id }: TutorialSectionHeaderProps) {
  return (
    <div id={id} className="scroll-mt-20">
      <div className="mb-1.5 font-mono text-xs uppercase tracking-widest text-primary">
        {label}
      </div>
      <h2 className="mb-5 border-b border-border pb-3 text-xl font-semibold text-foreground">
        {title}
      </h2>
    </div>
  );
}
